import cn from 'clsx';
import { LibraryRiskFieldsExtended } from '@interfaces/library-risk.interface';
import { GridCustomHeaderCellProps, GridCustomRowProps, GridHandle } from '@progress/kendo-react-grid';
import _ from 'lodash';
import React, { RefObject } from 'react';
import { GRID_ROW_TEST_ID_MASTER } from '@utils/automation-test-helpers';

export const CUSTOM_COLUMN_WIDTH = 32;

type ContextProps = {
    reorder: (dataItem: LibraryRiskFieldsExtended, direction: 'before' | 'after' | null) => void;
    dragStart: (dataItem: LibraryRiskFieldsExtended) => void;
};

export const ReorderContext = React.createContext<ContextProps>({
    reorder: () => {},
    dragStart: () => {},
});

export const CustomRow = (props: GridCustomRowProps) => {
    const { trProps, children, absoluteRowIndex } = props;
    const trCustomProps: Record<string, string | number> = {
        'data-grid-row-index': absoluteRowIndex,
        'data-testid': GRID_ROW_TEST_ID_MASTER,
    };

    return (
        <tr {...trProps} {...trCustomProps} className={cn(trProps?.className, props?.isAltRow ? 'k-alt' : '')}>
            {children}
        </tr>
    );
};

export const GetFilterDirIcon = ({ thProps }: GridCustomHeaderCellProps) => {
    return thProps?.ariaSort === 'none' ? (
        ''
    ) : (
        <span className="k-sort-icon">
            <span className="k-icon k-svg-icon k-svg-i-sort-asc-small" aria-hidden="true">
                <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    {thProps?.ariaSort === 'ascending' && <path d="M256 192v224h-32V192h-96L240 64l112 128z" />}
                    {thProps?.ariaSort === 'descending' && <path d="M352 288 240 416 128 288h96V64h32v224z" />}
                </svg>
            </span>
        </span>
    );
};

// If detail={...} is not equal to null for a given grid then kendo will automatically add a hierarchy column to the start of the grid.
// This col will be fixed at 32px and will contain a + icon for expanding the detail row.
// We are overwriting the column with our own custom cell as it seems impossible to just hide that auto-generated column without causing issues
// such as undesired column widths and fixed columns not working correctly etc.
// Our custom column can contain any combination of expansion icon, row selection checkbox and 3 dot action menu so we need to override that
// hard-coded 32px column width depending on how my controls are inside a given column.
export const calculateHierarchyRowWidth = (gridRef: RefObject<GridHandle | null>) => {
    // Get all rows in the table
    const rows = gridRef.current?.element?.getElementsByTagName('tr');

    const hasHierarchyRows: HTMLCollectionOf<Element> | undefined = gridRef.current?.element?.getElementsByClassName(
        'k-grid-content-sticky k-hierarchy-cell',
    );
    const hasMulitpleDetailControls: HTMLCollectionOf<Element> | undefined =
        gridRef.current?.element?.getElementsByClassName('k-hierarchy-cell multiple-inputs');

    // Loop through them all...
    _.forEach(rows, (row: HTMLTableRowElement) => {
        // Find the hierarchy cell
        const hierarchyCell = row.querySelector('.k-hierarchy-cell');
        if (row.children.length > 0 && hierarchyCell) {
            // Get the col-index of the cell
            const colIdToArrayIdx = +hierarchyCell.ariaColIndex! - 1;

            // Get the colGroup element that is related to the cell. Have to use this selection method as
            // detail rows can also contain tables so we need to get the related colgroup
            const colGroup = row.parentElement?.previousElementSibling;

            if (colGroup) {
                // remove any styling from ALL col group items to reset the widths.
                // this is required to handle cases where the user groups rows and then removes the grouping,
                // incorrectly reducing the width of the first column (columns if multiple grouping are removed) after the hierarchy column
                for (let i = 0; i < colGroup.children.length; i++) {
                    (colGroup.children[i] as HTMLTableColElement).style.width = '';
                }

                // Reset any previously applied style overrides
                if (colIdToArrayIdx > -1) (colGroup.children[0] as HTMLTableColElement).style.width = '';

                // Calculate the width of the column based on the number of inputs and update the colGroup items width
                if (hasMulitpleDetailControls && hasMulitpleDetailControls.length > 0) {
                    // Adding this class will allow us to hide any rows that are empty using custom CSS
                    hierarchyCell.classList.add('advanced-detail-row');

                    const inputCount = +(
                        hasMulitpleDetailControls[0]?.children[0]?.getAttribute('data-inputcount') ?? '1'
                    );
                    if (colIdToArrayIdx > -1)
                        (colGroup.children[colIdToArrayIdx] as HTMLTableColElement).style.width = `${
                            inputCount * CUSTOM_COLUMN_WIDTH
                        }px`;
                } else if (hasHierarchyRows && hasHierarchyRows.length > 0 && colIdToArrayIdx > -1) {
                    if (hierarchyCell.getAttribute('data-width')) {
                        (colGroup.children[colIdToArrayIdx] as HTMLTableColElement).style.width =
                            `${hierarchyCell.getAttribute('data-width')}px`;
                    } else (colGroup.children[colIdToArrayIdx] as HTMLTableColElement).style.width = '42px';
                }
            }
        }
    });
};
